import * as React from 'react';
// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputAcknowledgement,
	UseInputAcknowledgementResults,
	useInputDisqualifiersText,
	UseInputDisqualifiersTextResults,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupProps,
	NavigationFooterProps,
	FormPopupAcknowledgementsProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';
// Import api models.
import {
	AcknowledgementText,
	DisqualifiersTextWithAcknowledgement,
	DisqualifyingConditionsAgreement,
} from '@app/models/api';

interface UseFormPopupAcknowledgementsParameters {
	id: string;
	props: FormPopupAcknowledgementsProps,
}

interface UseFormPopupAcknowledgementsResults {
	submitForm: () => void;
	showDisqualifiersTextInput: boolean;
	showDisclosuresInput: boolean;
	disqualifiersTextInput: UseInputDisqualifiersTextResults;
	disclosuresInput: UseInputAcknowledgementResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupAcknowledgements = (params: UseFormPopupAcknowledgementsParameters): UseFormPopupAcknowledgementsResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			params.props.onSaveAcknowledgements(disclosuresInput.acknowledgements, disqualifiersTextInput.value);
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Acknowledgements',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Submit',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}


	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<DisqualifyingConditionsAgreement | undefined>(disqualifiersTextInput, focusToElement)
			&& disclosuresInput.checkValidationWithFocus();
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		disqualifiersTextInput.changeShowValidationFlag(true);
		disclosuresInput.changeShowValidationFlag(true);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for the disqualifiers text. 
	 **/
	const generateIdForDisqualifiersText = (): string => {
		return `${generateIdForForm()}-disqualifiersText`;
	}

	/**
	 * Generate id for acknowledgements. 
	 **/
	const generateIdForDisclosures = (): string => {
		return `${generateIdForForm()}-disclosures`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const showValidations = React.useMemo(() => {
		return params.props.showValidationsOnMount ?? false;
	}, [params.props.showValidationsOnMount]);

	const disqualifiersText: DisqualifiersTextWithAcknowledgement = React.useMemo(() => {
		return params.props.disqualifiersText ?? new DisqualifiersTextWithAcknowledgement();
	}, [params.props.disqualifiersText]);

	const initialDisqualifierAnswer: DisqualifyingConditionsAgreement | undefined = React.useMemo(() => {
		let answer: DisqualifyingConditionsAgreement | undefined = undefined;
		if (params.props.disqualifiersText !== undefined
			&& params.props.disqualifiersText.isAcknowledged) {
			answer = {
				riskMeetsEligibilityRequirements: params.props.disqualifiersText.isAcknowledged,
				version: params.props.disqualifiersText.version,
			};
		}
		return answer;
	}, [params.props.disqualifiersText]);

	const initialAcknowledgements: AcknowledgementText[] = React.useMemo(() => {
		return params.props.possibleDisclosureTexts.map<AcknowledgementText>((possibleDisclosureText) => {
			return {
				textId: possibleDisclosureText.id,
				isAcknowledged: possibleDisclosureText.isAcknowledged,
			}
		});
	}, [params.props.possibleDisclosureTexts]);

	// #region Input settings visibility.

	const showDisqualifiersTextInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.disqualifiersTextInput !== 'hidden'
		&& params.props.disqualifiersText !== undefined,
		[params.props.inputSettingsProps?.disqualifiersTextInput]
	);
	const showDisclosuresInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.disclosureInputs !== 'hidden'
		&& params.props.possibleDisclosureTexts.length > 0,
		[params.props.inputSettingsProps?.disclosureInputs]
	);

	// #endregion Input settings visibility.

	// #region Input settings disable.

	const isDisqualifiersTextInputDisabled: boolean = React.useMemo(() =>
		!showDisqualifiersTextInput
		|| params.props.inputSettingsProps?.disqualifiersTextInput === 'disabled',
		[
			showDisqualifiersTextInput,
			params.props.inputSettingsProps?.disqualifiersTextInput
		]
	);
	const isDisclosureInputsDisabled: boolean = React.useMemo(() =>
		!showDisclosuresInput
		|| params.props.inputSettingsProps?.disclosureInputs === 'disabled',
		[
			showDisclosuresInput,
			params.props.inputSettingsProps?.disclosureInputs
		]
	);

	// #endregion Input settings disable

	// Setup the base form. 
	const disqualifiersTextInput: UseInputDisqualifiersTextResults = useInputDisqualifiersText({
		id: generateIdForDisqualifiersText(),
		label: '',
		required: true,
		disabled: isDisqualifiersTextInputDisabled,
		disqualifiersText: disqualifiersText,
		disqualifiersAnswer: initialDisqualifierAnswer,
		showValidation: params.props.showValidationsOnMount,
	});

	// Instantiate the 'dislcosures' inputs hooks.
	let disclosuresInput: UseInputAcknowledgementResults = useInputAcknowledgement({
		id: generateIdForDisclosures(),
		disabled: isDisclosureInputsDisabled,
		required: true,
		acknowledgements: initialAcknowledgements,
		showValidation: params.props.showValidationsOnMount,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		if (!showValidations
			|| checkFormValidation(true)) {
			if (!isDisqualifiersTextInputDisabled) {
				disqualifiersTextInput.focusToInput();
			}
			else if (!isDisclosureInputsDisabled) {
				// If validations pass or are not to be shown, then initially the first input should be focused.
				if (params.props.possibleDisclosureTexts.length > 0
					&& params.props.possibleDisclosureTexts[0].id !== null) {
					// Focus to the first disclosure text input.
					disclosuresInput.focusToAcknowledgementInput(params.props.possibleDisclosureTexts[0].id)
				}
			}
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm,
		showDisqualifiersTextInput,
		showDisclosuresInput,
		disqualifiersTextInput,
		disclosuresInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupAcknowledgementsParameters,
}

// Export results.
export {
	UseFormPopupAcknowledgementsResults,
}

export default useFormPopupAcknowledgements;